import { ArrowCcwIcon, EditIcon, PlusIcon } from "@xola/icons";
import { Button, InlineValuePopover, Label, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { InputField, Loader, SecondaryButton } from "common";
import { useFormikContext } from "formik";
import { keyBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    AmountType,
    CatalogItemObject,
    Experience,
    FieldWithPickupOptions,
    PickupCatalogItem,
    QuestionnairePricingStructure,
} from "types";
import { AddPickupLocationsModal, PickupOption } from "./AddPickupLocationsModal";
import { TimePickerPopover } from "@/components/Common/TimePickerPopover";
import { CurrencyInputField } from "@/components/Field/CurrencyInputField";
import { formatPickupPrice } from "@/components/Questionnaire/PickupLocationSelectField";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useModal, useVisibility } from "@/hooks/useVisibility";

interface OptionWithSequence {
    sequence: number;
    pickupLocation: { id: string };
    localOverrides?: any;
}

export const PickupLocationsList = ({ experience }: { experience?: Experience }) => {
    const { values, errors, submitCount, setFieldValue } = useFormikContext<FieldWithPickupOptions>();
    const addLocationModal = useModal();
    const { data: pickupLocationsList, isLoading } = useAutoFetchCollection<PickupCatalogItem>(
        "catalogItems",
        { object: CatalogItemObject.PICKUP_CATALOG_ITEM },
        {},
    );

    const { data: pickupLocationsProductList, isLoading: isLoadingProductLocations } =
        useAutoFetchCollection<PickupCatalogItem>(
            "catalogItems",
            { object: CatalogItemObject.PICKUP_PRODUCT_CATALOG_ITEM },
            {},
        );

    const optionsList = useMemo(() => values?.options ?? [], [values]);
    const isOptionsEmpty = !optionsList || optionsList.length === 0;
    const isOptionsEmptyWarning = Boolean(errors.options) && isOptionsEmpty && submitCount > 0;

    // Flag to determine if prices and timing should be editable
    const isEditable = !!experience;

    const filteredList = useMemo(() => {
        if (!pickupLocationsList) return [];

        const validOptions = (optionsList as OptionWithSequence[]).filter((opt) => opt?.pickupLocation?.id);

        const optionsMap: Record<string, OptionWithSequence> = keyBy(validOptions, (opt) => opt.pickupLocation.id);

        const combined = [...pickupLocationsList, ...pickupLocationsProductList];

        const hasAnySequence = validOptions.some((option) => typeof option?.sequence === "number");

        return combined
            .filter((loc) => optionsMap[loc.id])
            .sort((a, b) => {
                const optA = optionsMap[a.id];
                const optB = optionsMap[b.id];

                if (hasAnySequence) {
                    const seqA = optA?.sequence ?? 0;
                    const seqB = optB?.sequence ?? 0;
                    return seqA - seqB;
                }

                const indexA = validOptions.findIndex((option) => option.pickupLocation.id === a.id);
                const indexB = validOptions.findIndex((option) => option.pickupLocation.id === b.id);

                return indexA - indexB;
            });
    }, [optionsList, pickupLocationsList, pickupLocationsProductList]);

    const handleSubmit = (selectedItems: PickupOption[]) => {
        setFieldValue("options", [...selectedItems]);

        addLocationModal.toggle();
        return selectedItems;
    };

    const getParentLocation = useCallback(
        (id?: string) => {
            return pickupLocationsList.find((item) => item.id === id);
        },
        [pickupLocationsList],
    );

    if (isLoading || isLoadingProductLocations) return <Loader />;

    return (
        <>
            <div className="mb-2 flex items-center justify-between">
                <div>
                    <Label>Locations</Label>
                    {isOptionsEmptyWarning && isOptionsEmpty && (
                        <p className="text-sm text-danger">At least one pickup location is required</p>
                    )}
                </div>

                {!isOptionsEmpty && (
                    <SecondaryButton
                        size="small"
                        icon={<EditIcon />}
                        className="w-[100px]"
                        data-testid="questionnaire-pickup-locations-edit-button"
                        onClick={addLocationModal.toggle}
                    >
                        Edit
                    </SecondaryButton>
                )}
            </div>

            <div className="max-h-[200px] overflow-y-auto py-2">
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>Name</Table.Header>
                            <Table.Header>
                                <span className="flex items-center justify-end">
                                    {!isOptionsEmpty
                                        ? values.priceType === QuestionnairePricingStructure.PER_GROUP
                                            ? "Price per group"
                                            : "Price per person"
                                        : "Price"}
                                </span>
                            </Table.Header>
                            <Table.Header>
                                <span className="flex items-center justify-end">Timing</span>
                            </Table.Header>
                        </Table.Row>
                    </Table.Head>
                    <Table.Body className={clsx("relative", isOptionsEmpty && "h-[100px]")}>
                        {isOptionsEmpty ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                                <p>Add locations here to get started</p>
                                <SecondaryButton
                                    size="small"
                                    icon={<PlusIcon />}
                                    className="w-[100px]"
                                    data-testid="questionnaire-pickup-locations-add-button"
                                    onClick={addLocationModal.toggle}
                                >
                                    Add
                                </SecondaryButton>
                            </div>
                        ) : (
                            filteredList.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-testid="questionnaire-pickup-location-row"
                                    row={row}
                                    priceType={values.priceType}
                                    parentLocation={getParentLocation(row.parent?.id)}
                                    isEditable={isEditable}
                                    onOverride={(overrides) => {
                                        setFieldValue(
                                            "options",
                                            values.options.map((opt) =>
                                                opt.pickupLocation.id === row.id
                                                    ? {
                                                          ...opt,
                                                          localOverrides: overrides,
                                                      }
                                                    : opt,
                                            ),
                                        );
                                    }}
                                />
                            ))
                        )}
                    </Table.Body>
                </Table>
            </div>

            <AddPickupLocationsModal
                isOpen={addLocationModal.isVisible}
                selectedItems={optionsList}
                onSubmit={handleSubmit}
                onClose={addLocationModal.toggle}
            />
        </>
    );
};

const TableRow = ({
    row,
    parentLocation,
    priceType,
    isEditable,
    onOverride,
}: {
    row: PickupCatalogItem;
    parentLocation?: PickupCatalogItem;
    priceType?: QuestionnairePricingStructure;
    isEditable?: boolean;
    onOverride?: (overrides: { prices?: PickupCatalogItem["prices"]; arrivalOffset?: number }) => void;
}) => {
    const { values } = useFormikContext<FieldWithPickupOptions>();

    const [visible, { toggle }] = useVisibility(false);

    const overrideData = values.options.find((option) => option.pickupLocation.id === row.id);

    const [localOverrides, setLocalOverrides] = useState<{
        prices?: PickupCatalogItem["prices"];
        arrivalOffset?: number;
    }>({
        prices: overrideData?.localOverrides?.prices ?? row.prices,
        arrivalOffset: overrideData?.localOverrides?.arrivalOffset ?? Number(row.arrivalOffset),
    });

    const [inputPrice, setInputPrice] = useState(
        localOverrides.prices?.[priceType === QuestionnairePricingStructure.PER_GROUP ? "perGroup" : "perPerson"]
            ?.price ?? 0,
    );

    const seller = useSeller();
    const currency = seller?.currency;

    const priceKey = priceType === QuestionnairePricingStructure.PER_GROUP ? "perGroup" : "perPerson";
    const currentPrice = localOverrides.prices?.[priceKey]?.price;
    const originalPriceType = row.prices?.[priceKey]?.priceType;

    const handleApply = () => {
        const newPrices = {
            ...localOverrides.prices,
            [priceKey]: {
                ...localOverrides.prices?.[priceKey],
                price: inputPrice,
            },
        };
        const newOverrides = { ...localOverrides, prices: newPrices };
        setLocalOverrides(newOverrides);
        onOverride?.(newOverrides);
        toggle();
    };

    const handleResetPrice = () => {
        const base = parentLocation ?? row;
        setInputPrice(base.prices?.[priceKey]?.price ?? 0);
        const newPrices = {
            ...localOverrides.prices,
            [priceKey]: base.prices?.[priceKey],
        };
        const newOverrides = { ...localOverrides, prices: newPrices };
        setLocalOverrides(newOverrides);
        onOverride?.(newOverrides);
    };

    const handleResetTime = () => {
        const base = parentLocation ?? row;
        const newOverrides = { ...localOverrides, arrivalOffset: Number(base.arrivalOffset) };
        setLocalOverrides(newOverrides);
        onOverride?.(newOverrides);
    };

    const handleArrivalOffsetChange = (value: number) => {
        const newOverrides = { ...localOverrides, arrivalOffset: value };
        setLocalOverrides(newOverrides);
        onOverride?.(newOverrides);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        value = value.replace(/\D+/g, "");

        setInputPrice(Number(value));
    };

    useEffect(() => {
        const prices = overrideData?.localOverrides?.prices ?? row.prices;
        const arrivalOffset = overrideData?.localOverrides?.arrivalOffset ?? Number(row.arrivalOffset);

        setLocalOverrides({ prices, arrivalOffset });

        const price = overrideData?.localOverrides?.prices?.[priceKey]?.price ?? row.prices?.[priceKey]?.price ?? 0;

        setInputPrice(price);
    }, [row, overrideData, priceKey]);

    const formattedPrice = formatPickupPrice(
        localOverrides,
        currency,
        priceType === QuestionnairePricingStructure.PER_GROUP,
    );

    return (
        <Table.Row
            key={row.id}
            data-testid="questionnaire-pickup-location-row"
            className="border-0 border-b border-b-gray-light last:border-b-0"
        >
            <Table.Cell className="max-w-[170px]">
                <p className="whitespace-normal break-keep">{row.name}</p>
            </Table.Cell>

            <Table.Cell>
                <div className="flex items-center justify-end space-x-1">
                    {isEditable &&
                        (() => {
                            const base = parentLocation ?? row;
                            const basePrice = base.prices?.[priceKey]?.price;
                            return currentPrice !== basePrice ? (
                                <SecondaryButton
                                    title="Reset"
                                    size="tiny"
                                    data-testid="questionnaire-pickup-location-price-reset-button"
                                    onClick={handleResetPrice}
                                >
                                    <ArrowCcwIcon />
                                </SecondaryButton>
                            ) : null;
                        })()}

                    {isEditable ? (
                        <InlineValuePopover
                            placement="left"
                            disabled={row.parent ? false : row.isDeclinePickupItem}
                            text={formattedPrice}
                            isOpen={visible}
                            showArrow={false}
                            classNames={{ text: "!whitespace-normal no-translate" }}
                            data-testid="questionnaire-pickup-location-price-cell"
                            onClick={toggle}
                            onClickOutside={toggle}
                        >
                            <div className="!m-0 flex flex-row space-x-2">
                                {originalPriceType === AmountType.PERCENT ? (
                                    <InputField
                                        min={0}
                                        value={inputPrice}
                                        type="number"
                                        className="block w-[100px]"
                                        data-testid="questionnaire-pickup-location-price-input"
                                        onChange={handleChange}
                                    />
                                ) : (
                                    <CurrencyInputField
                                        min={0}
                                        value={inputPrice}
                                        className="block w-[100px] "
                                        data-testid="questionnaire-pickup-location-price-input"
                                        onChange={handleChange}
                                    />
                                )}

                                <Button
                                    type="button"
                                    data-testid="questionnaire-pickup-location-price-apply-button"
                                    onClick={handleApply}
                                >
                                    Apply
                                </Button>
                            </div>
                        </InlineValuePopover>
                    ) : (
                        <span className="no-translate whitespace-normal">{formattedPrice}</span>
                    )}
                </div>
            </Table.Cell>

            <Table.Cell>
                {Number(row.arrivalOffset) > 0 ? (
                    <div className="flex items-center justify-end space-x-1">
                        {isEditable &&
                            (() => {
                                const base = parentLocation ?? row;
                                return localOverrides.arrivalOffset !== base.arrivalOffset ? (
                                    <SecondaryButton
                                        title="Reset"
                                        size="tiny"
                                        data-testid="questionnaire-pickup-location-time-reset-button"
                                        onClick={handleResetTime}
                                    >
                                        <ArrowCcwIcon />
                                    </SecondaryButton>
                                ) : null;
                            })()}

                        <TimePickerPopover
                            showInHours
                            placement="left"
                            submitOnApply={false}
                            disabled={!isEditable}
                            dataTestId="questionnaire-pickup-location-time-cell"
                            value={localOverrides.arrivalOffset ?? 0}
                            onApply={handleArrivalOffsetChange}
                        />
                    </div>
                ) : (
                    <div className="flex items-center justify-end space-x-1">--</div>
                )}
            </Table.Cell>
        </Table.Row>
    );
};
